import React, { useCallback, lazy, Suspense } from 'react';

import {
  useUserActions,
  useUserPages,
  useLoading,
  UserGuard,
  NoUser,
  AuthForm,
  AccessDeniedWithRedirect,
} from 'Auth';
import { NotificationsSettingsRedirect } from 'components/NotificationsSettings';

import Permissions from '@gogame/permissions';
import { MessageProvider } from '@gogame/screens';
import { ScreenProvider, Screens } from '@gogame/screens';

import Application from './Application';
import AdBlockChecker from './components/AdBlockChecker';
import { SubscriptionsRedirect } from './components/Subscriptions';
import { SuspenseFallback } from './components/Suspense';
import { ScreenWrapper } from './components/WebSocket';
import { isGoGameEnv } from './constants';
import { Sentry } from '@gogame/components';

const LazyRoutes = lazy(
  () =>
    import(
      /* webpackChunkName: "[routes]" */ /* webpackPrefetch: true */ 'scenes/routes'
    )
);
const LazyPartnerRoutes = lazy(() => {
  return import(
    /* webpackChunkName: "[partnerRoutes]" */ /* webpackPrefetch: true */ 'scenes/partnerRoutes'
  );
});

const UserScreenProvider = ({ children }) => {
  const userLoading = useLoading();
  return <ScreenProvider loading={userLoading}>{children}</ScreenProvider>;
};

const actionRouts = [
  {
    path: '/subscriptions/:screen',
    element: <SubscriptionsRedirect />,
  },
  {
    path: 'notifications/:screen',
    element: <NotificationsSettingsRedirect />,
  },
];

const Root = () => {
  const actions = useUserActions();
  const LazyScreens = isGoGameEnv ? LazyRoutes : LazyPartnerRoutes;
  const pages = useUserPages();

  const handleError = useCallback(
    (error, cause) => {
      console.log('handleError', error, cause);
      Sentry.captureError(error);
    },
    [Sentry.captureError]
  );

  return (
    <AdBlockChecker>
      <Permissions actions={actions}>
        <Suspense fallback={<SuspenseFallback />}>
          <LazyScreens>
            {({ screens, getDefaultScreenLink }) => {
              const defaultScreenLink = getDefaultScreenLink(pages);
              return (
                <UserScreenProvider>
                  <UserGuard>
                    <Screens
                      defaultKnowledgeBaseUrl={
                        'https://go-game.zendesk.com/hc/en-us/categories/360000950293-GoGame-User-Guides'
                      }
                      ids={pages}
                      screens={screens}
                      defaultScreenLink={defaultScreenLink}
                      NoAccess={AccessDeniedWithRedirect}
                      ScreenWrapper={ScreenWrapper}
                      basename={process.env.REACT_APP_PUBLIC_URL}
                      actionRouts={actionRouts}
                      createBrowserRouter={Sentry.sentryCreateBrowserRouter}
                      onError={handleError}
                      errorBoundary={Sentry.RouterErrorBoundary}
                    >
                      <MessageProvider>
                        <Application logoLink={defaultScreenLink} />
                      </MessageProvider>
                    </Screens>
                  </UserGuard>
                  <NoUser>
                    <AuthForm />
                  </NoUser>
                </UserScreenProvider>
              );
            }}
          </LazyScreens>
        </Suspense>
      </Permissions>
    </AdBlockChecker>
  );
};

export default Root;
